import Link from 'components/commercetools-ui/atoms/link';
import { useParams } from 'next/navigation';
import React, { memo, useEffect, useMemo } from 'react';
import { Reference } from 'types/reference';
import { BreadcrumbItem } from './types';

export interface BreadcrumbsUIProps {
  items: BreadcrumbItem[];
}

const BreadcrumbsUI: React.FC<BreadcrumbsUIProps> = ({ items }) => {
  const { locale } = useParams();
  const location = typeof window !== 'undefined' ? window.location : null;
  const itemLink = useMemo((): (Reference | null)[] => {
    return items.map((item) => {
      return typeof item.url === 'string'
        ? {
            link: /^http/.test(item.url) ? item.url : `${location?.origin}/${locale}/${item.url}`,
            type: 'link',
          }
        : null;
    });
  }, [items, locale]);

  const breadcrumbSchema = useMemo(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: /^http/.test(item.url) ? item.url : `${location?.origin}/${locale}/${item.url}`,
      })),
    };
    return JSON.stringify(schema);
  }, [items, locale]);

  useEffect(() => {
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.innerHTML = breadcrumbSchema;
    document.head.appendChild(tag);
    return () => {
      document.head.removeChild(tag);
    };
  }, [breadcrumbSchema]);

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <nav
      data-id="breadcrumbs"
      aria-label="Breadcrumb"
      className="mx-auto flex max-w-7xl px-24 pb-32 pt-20 sm:pt-24 lg:px-32 lg:pt-32"
    >
      <ol role="list" className="flex flex-wrap items-center gap-x-2 text-sm sm:gap-x-4">
        {items.map((item, index) => (
          <li key={item.label} className="flex items-center">
            {index > 0 && <span className="shrink-0 px-8 text-14 text-gray-300">/</span>}
            {itemLink[index] && index !== items.length - 1 ? (
              <Link data-id={`breadcrumb-${index}`} padi-analytics={item.dataAnalytics} link={itemLink[index]}>
                <span className="text-14 text-padi-blue">{item.label}</span>
              </Link>
            ) : (
              <span data-id={`breadcrumb-${index}`} className="text-14 text-padi-gray-darkest">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default memo(BreadcrumbsUI);
